import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../utils/api';

const SOURCE_LABELS = {
    'logic-chat': 'Logic chat'
};

function formatTokens(value) {
    return Number(value || 0).toLocaleString();
}

function sourceLabel(source) {
    return SOURCE_LABELS[source] || source || '—';
}

const emptyAi = { items: [], promptTokens: 0, completionTokens: 0, totalTokens: 0 };
const emptyWork = { items: [], totalTokens: 0, requestCount: 0, catalog: [] };

export default function UsageBilling() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [aiUsage, setAiUsage] = useState(emptyAi);
    const [workUsage, setWorkUsage] = useState(emptyWork);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const [aiRes, workRes] = await Promise.all([
                    apiFetch('/api/llm-usage'),
                    apiFetch('/api/work-usage')
                ]);
                const aiBody = await aiRes.json().catch(() => ({}));
                const workBody = await workRes.json().catch(() => ({}));
                if (!aiRes.ok) throw new Error(aiBody.message || 'Unable to load AI token usage.');
                if (!workRes.ok) throw new Error(workBody.message || 'Unable to load work token usage.');
                if (!cancelled) {
                    setAiUsage({
                        items: aiBody.items || [],
                        promptTokens: aiBody.promptTokens || 0,
                        completionTokens: aiBody.completionTokens || 0,
                        totalTokens: aiBody.totalTokens || 0
                    });
                    setWorkUsage({
                        items: workBody.items || [],
                        totalTokens: workBody.totalTokens || 0,
                        requestCount: workBody.requestCount || 0,
                        catalog: workBody.catalog || []
                    });
                }
            } catch (err) {
                if (!cancelled) setError(err.message || 'Unable to load usage.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const aiRows = useMemo(
        () => [...(aiUsage.items || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
        [aiUsage.items]
    );
    const workRows = useMemo(
        () => [...(workUsage.items || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
        [workUsage.items]
    );

    return (
        <div style={{ padding: '1rem 1.25rem', maxWidth: '1200px', margin: '0 auto' }}>
            <h1 style={{ fontSize: '1.35rem', margin: 0 }}>Usage and billing</h1>
            <p style={{ color: 'var(--text-secondary)', marginTop: '0.35rem' }}>
                This organization currently has unlimited usage. AI tokens and work tokens are recorded for visibility; limits and invoices are not applied yet.
            </p>

            <div
                style={{
                    marginTop: '0.75rem',
                    padding: '1.25rem',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    background: 'var(--bg-secondary)'
                }}
            >
                <div style={{ fontWeight: 600 }}>Unlimited plan</div>
                <p style={{ margin: '0.4rem 0 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    There is no token cap or invoice right now. AI tokens come from language-model calls. Work tokens are charged per successful API request using the endpoint costs below.
                </p>
            </div>

            {error && (
                <div style={{ marginTop: '1rem', color: 'var(--error)' }}>{error}</div>
            )}

            {loading ? (
                <div style={{ marginTop: '2rem' }}>Loading…</div>
            ) : (
                <>
                    <Section title="AI token usage" description="Tokens used by Logic chat and other language-model features.">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                            <MetricCard label="Total tokens" value={formatTokens(aiUsage.totalTokens)} />
                            <MetricCard label="Prompt tokens" value={formatTokens(aiUsage.promptTokens)} />
                            <MetricCard label="Completion tokens" value={formatTokens(aiUsage.completionTokens)} />
                            <MetricCard label="Recorded requests" value={formatTokens(aiRows.length)} />
                        </div>
                        <UsageTable
                            empty="No AI token usage has been recorded yet. Logic chat and similar features will appear here."
                            columns={['When', 'Source', 'Model', 'Prompt', 'Completion', 'Total']}
                            rows={aiRows.map(row => ({
                                key: row.id,
                                cells: [
                                    row.createdAt ? new Date(row.createdAt).toLocaleString() : '—',
                                    sourceLabel(row.source),
                                    row.model || '—',
                                    formatTokens(row.promptTokens),
                                    formatTokens(row.completionTokens),
                                    formatTokens(row.totalTokens)
                                ],
                                alignRightFrom: 3
                            }))}
                        />
                    </Section>

                    <Section title="Work token usage" description="Successful product API calls. Each endpoint has a fixed work-token cost; reads are cheaper than writes, evaluates, imports, and publishes.">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                            <MetricCard label="Work tokens" value={formatTokens(workUsage.totalTokens)} />
                            <MetricCard label="API requests" value={formatTokens(workUsage.requestCount || workRows.length)} />
                        </div>
                        <UsageTable
                            empty="No work token usage has been recorded yet. Families, rules, evaluate, and other product APIs will appear here."
                            columns={['When', 'Method', 'Endpoint', 'Cost']}
                            rows={workRows.map(row => ({
                                key: row.id,
                                cells: [
                                    row.createdAt ? new Date(row.createdAt).toLocaleString() : '—',
                                    row.method || '—',
                                    row.label || row.endpoint || '—',
                                    formatTokens(row.cost)
                                ],
                                alignRightFrom: 3
                            }))}
                        />
                        <h3 style={{ margin: '1.5rem 0 0.75rem', fontSize: '1rem' }}>Endpoint costs</h3>
                        <p style={{ margin: '0 0 0.75rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                            Unlisted endpoints use {formatTokens(1)} for GET, {formatTokens(5)} for writes, and {formatTokens(3)} for DELETE. AI chat endpoints are billed as AI tokens only.
                        </p>
                        <UsageTable
                            empty="No endpoint costs are configured."
                            columns={['Method', 'Endpoint', 'Cost']}
                            rows={(workUsage.catalog || []).map(entry => ({
                                key: `${entry.method} ${entry.endpoint}`,
                                cells: [entry.method, entry.label || entry.endpoint, formatTokens(entry.cost)],
                                alignRightFrom: 2
                            }))}
                        />
                    </Section>
                </>
            )}
        </div>
    );
}

function Section({ title, description, children }) {
    return (
        <section style={{ marginTop: '2rem' }}>
            <h2 style={{ fontSize: '1.35rem', margin: 0 }}>{title}</h2>
            <p style={{ color: 'var(--text-secondary)', margin: '0.35rem 0 1rem', fontSize: '0.9rem' }}>{description}</p>
            {children}
        </section>
    );
}

function MetricCard({ label, value }) {
    return (
        <div style={{ padding: '1rem 1.15rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
            <div style={{ marginTop: '0.4rem', fontSize: '1.4rem', fontWeight: 600 }}>{value}</div>
        </div>
    );
}

const MAX_VISIBLE_ROWS = 10;
const ROW_HEIGHT = '2.75rem';

function UsageTable({ empty, columns, rows }) {
    const scrollable = rows.length > MAX_VISIBLE_ROWS;
    const alignRightFrom = rows[0]?.alignRightFrom ?? 99;
    return (
        <div style={{
            marginTop: '0.75rem',
            overflowX: 'auto',
            overflowY: scrollable ? 'auto' : undefined,
            maxHeight: scrollable ? `calc(${ROW_HEIGHT} * ${MAX_VISIBLE_ROWS + 1} + ${MAX_VISIBLE_ROWS}px + 2px)` : undefined,
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)'
        }}>
            {rows.length === 0 ? (
                <div style={{ padding: '1.5rem', color: 'var(--text-secondary)' }}>{empty}</div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.9rem' }}>
                    <thead>
                        <tr>
                            {columns.map((col, i) => (
                                <th key={col} style={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1,
                                    height: ROW_HEIGHT,
                                    textAlign: i >= alignRightFrom ? 'right' : 'left',
                                    padding: '0.75rem',
                                    background: 'var(--bg-secondary)',
                                    boxShadow: 'inset 0 -1px 0 var(--border)'
                                }}>{col}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.key}>
                                {row.cells.map((cell, i) => (
                                    <td key={`${row.key}-${i}`} style={{
                                        height: ROW_HEIGHT,
                                        padding: '0.75rem',
                                        textAlign: i >= (row.alignRightFrom ?? 99) ? 'right' : 'left',
                                        borderBottom: '1px solid var(--border)'
                                    }}>{cell}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
